import React, { useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Search, Plus, Edit, Trash2, Archive, CheckCircle } from 'lucide-react';
import { BillingItem } from '../../types';
export const ItemList: React.FC = () => {
  const {
    billingItems,
    addBillingItem,
    updateBillingItem,
    deleteBillingItem,
    currentUser,
    logBillingAudit
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<BillingItem | null>(null);
  const [formData, setFormData] = useState({
    itemCode: '',
    serviceName: '',
    category: 'Consultation',
    department: 'General',
    description: '',
    unitPrice: '',
    taxRate: '0',
    discountEligibility: true,
    status: 'Active' as 'Active' | 'Inactive' | 'Archived'
  });
  const isAdmin = currentUser?.role === 'Administrator';
  const filteredItems = billingItems.filter(i => i.serviceName.toLowerCase().includes(searchQuery.toLowerCase()) || i.itemCode.toLowerCase().includes(searchQuery.toLowerCase()) || i.category.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleOpenModal = (item?: BillingItem) => {
    if (item) {
      setEditingItem(item);
      setFormData({
        itemCode: item.itemCode,
        serviceName: item.serviceName,
        category: item.category,
        department: item.department,
        description: item.description || '',
        unitPrice: item.unitPrice.toString(),
        taxRate: item.taxRate.toString(),
        discountEligibility: item.discountEligibility,
        status: item.status
      });
    } else {
      setEditingItem(null);
      setFormData({
        itemCode: '',
        serviceName: '',
        category: 'Consultation',
        department: 'General',
        description: '',
        unitPrice: '',
        taxRate: '0',
        discountEligibility: true,
        status: 'Active'
      });
    }
    setShowModal(true);
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      itemCode: formData.itemCode,
      serviceName: formData.serviceName,
      category: formData.category,
      department: formData.department,
      description: formData.description,
      unitPrice: parseFloat(formData.unitPrice) || 0,
      taxRate: parseFloat(formData.taxRate) || 0,
      discountEligibility: formData.discountEligibility,
      status: formData.status
    };
    if (editingItem) {
      updateBillingItem(editingItem.id, payload);
      logBillingAudit('Update Item', `Updated billing item ${payload.itemCode}`);
    } else {
      addBillingItem({
        ...payload,
        createdAt: new Date().toISOString()
      });
      logBillingAudit('Create Item', `Created billing item ${payload.itemCode}`);
    }
    setShowModal(false);
  };
  const handleDelete = (id: string) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      const item = billingItems.find(i => i.id === id);
      deleteBillingItem(id);
      if (item) {
        logBillingAudit('Delete Item', `Deleted billing item ${item.itemCode}`);
      }
    }
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZM', {
      style: 'currency',
      currency: 'ZMW'
    }).format(amount);
  };
  if (!isAdmin) {
    return <div className="p-8 text-center text-red-600" data-id="element-1190">
        Only Administrators can manage the Item List.
      </div>;
  }
  return <div className="space-y-6" data-id="element-1191">
      <div className="flex justify-between items-center" data-id="element-1192">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1193">
          Item List (Service Catalogue)
        </h1>
        <Button onClick={() => handleOpenModal()} data-id="element-1194">
          <Plus className="w-4 h-4 mr-2" data-id="element-1195" /> Add Item
        </Button>
      </div>

      <Card noPadding data-id="element-1196">
        <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-1197">
          <div className="relative max-w-md" data-id="element-1198">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1199">
              <Search className="h-5 w-5 text-gray-400" data-id="element-1200" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name, code, or category..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1201" />
          </div>
        </div>

        <div className="overflow-x-auto" data-id="element-1202">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-1203">
            <thead className="bg-gray-50" data-id="element-1204">
              <tr data-id="element-1205">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1206">
                  Item Code
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1207">
                  Service Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1208">
                  Category
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1209">
                  Unit Price
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1210">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1211">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-1212">
              {filteredItems.length > 0 ? filteredItems.map(item => <tr key={item.id} className="hover:bg-gray-50" data-id="element-1213">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1214">
                      {item.itemCode}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-1215">
                      {item.serviceName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-1216">
                      {item.category}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900" data-id="element-1217">
                      {formatCurrency(item.unitPrice)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center" data-id="element-1218">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${item.status === 'Active' ? 'bg-green-100 text-green-800' : item.status === 'Inactive' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1219">
                        {item.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1220">
                      <button onClick={() => handleOpenModal(item)} className="text-blue-600 hover:text-blue-900 mr-3" title="Edit" data-id="element-1221">
                        <Edit className="w-4 h-4" data-id="element-1222" />
                      </button>
                      <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-1223">
                        <Trash2 className="w-4 h-4" data-id="element-1224" />
                      </button>
                    </td>
                  </tr>) : <tr data-id="element-1225">
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500" data-id="element-1226">
                    No items found.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Item Modal */}
      {showModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-1227">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6" data-id="element-1228">
            <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-1229">
              {editingItem ? 'Edit Item' : 'Add New Item'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1230">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1231">
                <Input label="Item Code" required value={formData.itemCode} onChange={e => setFormData({
              ...formData,
              itemCode: e.target.value
            })} placeholder="e.g., CONS-001" data-id="element-1232" />
                <Input label="Service Name" required value={formData.serviceName} onChange={e => setFormData({
              ...formData,
              serviceName: e.target.value
            })} data-id="element-1233" />
                <Select label="Category" required value={formData.category} onChange={e => setFormData({
              ...formData,
              category: e.target.value
            })} options={[{
              value: 'Consultation',
              label: 'Consultation'
            }, {
              value: 'Laboratory',
              label: 'Laboratory'
            }, {
              value: 'Radiology',
              label: 'Radiology'
            }, {
              value: 'Pharmacy',
              label: 'Pharmacy'
            }, {
              value: 'Procedures',
              label: 'Procedures'
            }, {
              value: 'Theatre',
              label: 'Theatre'
            }, {
              value: 'Maternity',
              label: 'Maternity'
            }, {
              value: 'Medical Reports',
              label: 'Medical Reports'
            }, {
              value: 'Occupational Health',
              label: 'Occupational Health'
            }, {
              value: 'Corporate Services',
              label: 'Corporate Services'
            }, {
              value: 'Administrative Charges',
              label: 'Administrative Charges'
            }, {
              value: 'Miscellaneous Services',
              label: 'Miscellaneous Services'
            }]} data-id="element-1234" />
                <Input label="Department" required value={formData.department} onChange={e => setFormData({
              ...formData,
              department: e.target.value
            })} data-id="element-1235" />
                <Input label="Unit Price (ZMW)" type="number" step="0.01" required value={formData.unitPrice} onChange={e => setFormData({
              ...formData,
              unitPrice: e.target.value
            })} data-id="element-1236" />
                <Input label="Tax Rate (%)" type="number" step="0.1" required value={formData.taxRate} onChange={e => setFormData({
              ...formData,
              taxRate: e.target.value
            })} data-id="element-1237" />
                <Select label="Status" required value={formData.status} onChange={e => setFormData({
              ...formData,
              status: e.target.value as any
            })} options={[{
              value: 'Active',
              label: 'Active'
            }, {
              value: 'Inactive',
              label: 'Inactive'
            }, {
              value: 'Archived',
              label: 'Archived'
            }]} data-id="element-1238" />
                <div className="flex items-center mt-6" data-id="element-1239">
                  <input id="discountEligibility" type="checkbox" checked={formData.discountEligibility} onChange={e => setFormData({
                ...formData,
                discountEligibility: e.target.checked
              })} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-1240" />
                  <label htmlFor="discountEligibility" className="ml-2 block text-sm text-gray-900" data-id="element-1241">
                    Eligible for Discount
                  </label>
                </div>
              </div>
              <div className="mt-4" data-id="element-1242">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1243">
                  Description (Optional)
                </label>
                <textarea className="w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" rows={3} value={formData.description} onChange={e => setFormData({
              ...formData,
              description: e.target.value
            })} data-id="element-1244" />
              </div>
              <div className="flex justify-end space-x-3 mt-6" data-id="element-1245">
                <Button variant="outline" type="button" onClick={() => setShowModal(false)} data-id="element-1246">
                  Cancel
                </Button>
                <Button type="submit" data-id="element-1247">
                  {editingItem ? 'Update Item' : 'Save Item'}
                </Button>
              </div>
            </form>
          </div>
        </div>}
    </div>;
};